// Pylon mounts its widget as direct children of <body>. When the chat is closed
// it collapses the window to 0x0 but leaves it in the accessibility tree, so
// screen readers read the whole support window. The class match covers a
// renamed container if Pylon changes its ids.
const PYLON_ROOT_SELECTOR = 'body > #pylon-chat, body > #pylon-frame, body > [class*="PylonChat"]';

/** Hides Pylon's widget from assistive tech (and keyboard focus) while it's closed. */
export function setPylonHiddenFromAT(hidden: boolean): void {
  document.querySelectorAll<HTMLElement>(PYLON_ROOT_SELECTOR).forEach((root) => {
    if (hidden) {
      if (!root.hasAttribute("inert")) root.setAttribute("inert", "");
      if (root.getAttribute("aria-hidden") !== "true") root.setAttribute("aria-hidden", "true");
    } else {
      root.removeAttribute("inert");
      root.removeAttribute("aria-hidden");
    }
  });
}

// null until Pylon reports in, so its first (immediate) callback sets a
// baseline instead of announcing messages the user already had.
let unreadCount: number | null = null;
const listeners = new Set<() => void>();

export function getPylonUnreadCount(): number {
  return unreadCount ?? 0;
}

/** Records Pylon's unread count and returns the previous one (null on the first report). */
export function setPylonUnreadCount(next: number): number | null {
  const previous = unreadCount;
  const safeNext = Number.isFinite(next) && next > 0 ? Math.floor(next) : 0;
  if (safeNext !== unreadCount) {
    unreadCount = safeNext;
    listeners.forEach((listener) => listener());
  }
  return previous;
}

export function subscribeToPylonUnreadCount(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Test-only. */
export function resetPylonUnreadCount(): void {
  unreadCount = null;
  listeners.clear();
}

/** What to tell screen reader users when the unread count changes, or null to stay quiet. */
export function unreadAnnouncement(previous: number | null, next: number): string | null {
  if (previous === null) return null;
  const added = next - previous;
  if (added <= 0) return null;
  return added === 1
    ? "New message from Knowtex Support"
    : `${added} new messages from Knowtex Support`;
}
